/*
  MONO configurator data — single source of truth.
  ------------------------------------------------------------
  Edit this file to:
    • add / remove colours (push into the appropriate `.colors` array)
    • add new sizes (push into `sizes`, drop the .glb in /assets,
      add its key in `models` + `heights`)
    • update prices, real-world filament names, supplier SKUs
    • adjust default selections and module count limits

  Every entry is a plain JS object — no Liquid, no JSON. Comments
  and trailing commas are fine. After editing, hard-refresh the
  configurator page to pick up changes.

  See MONO-BACKEND.md (repo root) for the full schema reference,
  the line-item-property contract sent to Shopify on Add-to-Cart,
  and the planned migration path to Shopify Metaobjects.
*/

// IMPORTANT — loaded as a plain (non-module) <script> tag so the
// asset_url cache-busting query string (?v=...) flows through and
// edits ship to the browser immediately. The configurator runtime
// reads `window.LaseConfiguratorConfig`. Do NOT switch this back to
// `export const LaseConfiguratorConfig` — relative imports inside other
// modules ignore Shopify's version param and serve stale data.
window.LaseConfiguratorConfig = {
  // ---------- 3D model files (kept in /assets) ----------
  models: {
    module_S:  'module_S.glb',
    module_M:  'module_M.glb',
    module_L:  'module_L.glb',
    connector: 'connector.glb'
  },

  // ---------- Real-world heights (metres) ----------
  // Used by Three.js for stacking math. Matches the dimensions
  // we print at — keep in sync with the GLB origin/scale.
  heights: {
    S: 0.070,
    M: 0.147,
    L: 0.209,
    connector: 0.045
  },

  // ---------- Sizes ----------
  //   id          — short key used everywhere in the runtime
  //   name        — customer-facing label
  //   heightMm    — printed height in mm (for invoices / packing)
  //   diameterMm  — widest external diameter in mm (for the spec
  //                  panel + 3D dimension overlay)
  //   weightG     — printed weight in grams. Auto-extraction from a
  //                  GLB isn't possible (depends on infill / walls /
  //                  density) so this is a hand-set value per part.
  //   sku         — Shopify / inventory SKU prefix for this size
  //   material    — base print material category (free text)
  //   price       — base price before finish modifier, EUR
  sizes: [
    { id: 'S', name: 'Small',  heightMm: 70,  diameterMm: 95, weightG: 165, sku: 'MONO-MOD-S', material: 'PLA', price: 45 },
    { id: 'M', name: 'Medium', heightMm: 147, diameterMm: 95, weightG: 280, sku: 'MONO-MOD-M', material: 'PLA', price: 65 },
    { id: 'L', name: 'Large',  heightMm: 209, diameterMm: 95, weightG: 410, sku: 'MONO-MOD-L', material: 'PLA', price: 90 }
  ],

  // ---------- Connector (the silicone joiner) ----------
  connector: {
    sku:        'MONO-CONN-01',
    heightMm:   45,
    diameterMm: 85,
    weightG:    25,
    material:   'TPU 95A (food-safe)',
    price:      15
  },

  // ---------- Shapes ----------
  //   id             — runtime key
  //   name           — customer label
  //   skuSuffix      — appended to module SKU on the order (e.g. -LN)
  //   ready          — only models marked ready render in 3D
  //   hint           — tooltip text in the picker UI
  //   priceModifier  — EUR added on top of size + finish for this
  //                    shape (0 for the default Lantern, future
  //                    shapes can carry an upcharge)
  shapes: [
    { id: 'lantern',  name: 'Lantern',  skuSuffix: '-LN', ready: true,  priceModifier: 0, hint: 'MONO classic' },
    { id: 'cylinder', name: 'Cylinder', skuSuffix: '-CY', ready: false, priceModifier: 0, hint: 'Coming soon'  },
    { id: 'sphere',   name: 'Sphere',   skuSuffix: '-SP', ready: false, priceModifier: 0, hint: 'Coming soon'  }
  ],

  // ---------- Finishes (Matte / Glow) ----------
  //   id            — runtime key
  //   name          — customer label
  //   skuSuffix     — appended to module SKU (e.g. -M, -G)
  //   material      — real filament category (PLA Matte, PETG, ...)
  //   priceModifier — adds onto the base size price (EUR)
  //   hint          — tooltip in the picker
  //   colors        — array of colour entries; each has:
  //       id           — runtime key (lowercase, kebab-case)
  //       name         — customer-facing label
  //       ext          — preview RGB for the OUTER shell, hand-tuned
  //                      to match a real printed sample as closely
  //                      as possible
  //       int          — preview RGB for the INNER cup
  //       filament     — real supplier filament name (free text;
  //                      shown on the print job ticket)
  //       filamentSku  — supplier/internal SKU for ordering filament
  finishes: [
    {
      id: 'matte',
      name: 'Matte',
      skuSuffix: '-M',
      material: 'PLA Matte',
      priceModifier: 0,
      hint: 'Solid PLA — opaque, colour-through.',
      colors: [
        { id: 'white',  name: 'White',  ext: '#f4f0e7', int: '#fbf8f0',
          filament: 'Polymaker PolyTerra Cotton White', filamentSku: 'PT-WHT-1KG' },
        { id: 'cobalt', name: 'Cobalt', ext: '#3b5b8b', int: '#dee4ec',
          filament: 'Polymaker PolyLite Galaxy Blue',   filamentSku: 'PL-GBL-1KG' },
        { id: 'moss',   name: 'Moss',   ext: '#3f5b3a', int: '#dde2d5',
          filament: 'Polymaker PolyTerra Forest Green', filamentSku: 'PT-FGN-1KG' },
        { id: 'ember',  name: 'Ember',  ext: '#a64a2e', int: '#f1d7c8',
          filament: 'Polymaker PolyTerra Lava Red',     filamentSku: 'PT-LVR-1KG' },
        { id: 'ink',    name: 'Ink',    ext: '#1a1c20', int: '#cfcfcf',
          filament: 'Polymaker PolyTerra Charcoal Black', filamentSku: 'PT-BLK-1KG' },
        { id: 'smoke',  name: 'Smoke',  ext: '#7d7e7c', int: '#dadbd9',
          filament: 'Polymaker PolyTerra Fossil Grey',  filamentSku: 'PT-FGR-1KG' }
      ]
    },
    {
      id: 'glow',
      name: 'Glow',
      skuSuffix: '-G',
      material: 'PETG Translucent',
      priceModifier: 4,
      hint: 'Translucent PETG — lights up in night mode.',
      colors: [
        { id: 'marigold', name: 'Marigold', ext: '#c9a449', int: '#f5e2b6',
          filament: 'Polymaker PolyLite PETG Translucent Yellow', filamentSku: 'PG-TYL-1KG' },
        { id: 'sun',      name: 'Sun',      ext: '#e3b450', int: '#f6dca3',
          filament: 'Polymaker PolyLite PETG Translucent Orange', filamentSku: 'PG-TOR-1KG' },
        { id: 'bone',     name: 'Bone',     ext: '#d8d2c4', int: '#f8f4ea',
          filament: 'Polymaker PolyLite PETG Translucent Natural', filamentSku: 'PG-TNT-1KG' },
        { id: 'coral',    name: 'Coral',    ext: '#d76b3a', int: '#f5d4b8',
          filament: 'Polymaker PolyLite PETG Translucent Coral',   filamentSku: 'PG-TCR-1KG' },
        { id: 'amber',    name: 'Amber',    ext: '#b76d2d', int: '#f0d4b0',
          filament: 'Polymaker PolyLite PETG Translucent Amber',   filamentSku: 'PG-TAM-1KG' }
      ]
    }
  ],

  // ---------- Connector colours (TPU palette is smaller) ----------
  //   id, name, hex_ext, filament, filamentSku  — same as above
  connectorColors: [
    { id: 'white',  name: 'White',  ext: '#f4f0e7',
      filament: 'Generic TPU 95A White', filamentSku: 'TPU-WHT-500G' },
    { id: 'black',  name: 'Black',  ext: '#1a1916',
      filament: 'Generic TPU 95A Black', filamentSku: 'TPU-BLK-500G' },
    { id: 'bone',   name: 'Bone',   ext: '#d8d2c4',
      filament: 'Generic TPU 95A Bone',  filamentSku: 'TPU-BNE-500G' },
    { id: 'sage',   name: 'Sage',   ext: '#6e8a6f',
      filament: 'Generic TPU 95A Sage',  filamentSku: 'TPU-SGE-500G' },
    { id: 'cobalt', name: 'Cobalt', ext: '#3b5b8b',
      filament: 'Generic TPU 95A Cobalt', filamentSku: 'TPU-CBL-500G' }
  ],

  // ---------- Light add-ons ----------
  //   id, name        — runtime key + customer label
  //   sku             — SKU sent on the order
  //   price           — EUR; 0 for "no light"
  //   ready           — only ready ones are clickable in UI
  //   needsGlow       — locked unless the stack has a Glow finish
  //   material        — short description used on the print/pack ticket
  lightAddons: [
    { id: 'none',     name: 'No light',    sku: '',                 price: 0,  weightG: 0,  ready: true,  needsGlow: false },
    { id: 'led-puck', name: 'LED Puck',    sku: 'MONO-LED-PUCK',    price: 29, weightG: 60, ready: false, needsGlow: true,
      material: 'USB-C battery LED puck, 5500 K' },
    { id: 'bulb',     name: 'Bulb Module', sku: 'MONO-BULB-E14',    price: 49, weightG: 95, ready: false, needsGlow: true,
      material: 'E14 socket housing + 4 W LED bulb' }
  ],

  // ---------- Defaults / limits ----------
  // A lamp is at least two modules (S+M or M+M upward) — and never
  // S-only in any count; the runtime additionally enforces that at
  // least one module is M or L (see keepsBody in configurator.js).
  minModules: 2,
  // Hard safety ceiling so a broken maxHeightMm calc never lets the
  // stack run away — but the binding constraint is maxHeightMm below.
  maxModules: 12,
  // Maximum stack height in mm as the SUM OF MODULE HEIGHTS ONLY —
  // the runtime's compact height ignores connectors (they nest inside
  // the modules), so this cap must be expressed the same way.
  // 565 = L + L + M (209·2 + 147), the tallest build approved for the
  // standard base. Taller builds wait for the Stability Base.
  maxHeightMm: 565,
  defaultModuleCount: 3,
  defaultSize: 'M',
  defaultShape: 'lantern',
  defaultFinish: 'matte',
  defaultModuleColorIdx:    2,   // Moss
  defaultConnectorColorIdx: 0,   // White

  // ---------- View-mode gap spacing (metres) ----------
  gaps: {
    compact:  0,
    semi:     0.015,
    exploded: 0.060
  }
};

// Convenience: legacy field shim so the rest of the runtime
// can still read the connector's price as `connectorPrice`.
window.LaseConfiguratorConfig.connectorPrice = window.LaseConfiguratorConfig.connector.price;
